import { Fragment, type ReactElement, type ReactNode } from "react";

import { worstCaseTemplateLength } from "../../template";
import type { PanelTemplateWarning } from "../../panel-contract";
import type { IconName } from "./Icon";
import type { ChatOutputTarget } from "../../contracts/values";
import type { MessageEnabledField } from "../../contracts/resolve-message-enabled";
import { ChoiceCards } from "./ChoiceCards";
import { ChatTargetField } from "./ChatTargetField";
import { Field } from "./Field";
import { FieldError } from "./FieldError";
import { InspectorFieldRow, InspectorSection } from "./InspectorParts";
import { UnitInput } from "./UnitInput";
import { Select } from "./Select";
import { SegmentChoice } from "./SegmentChoice";
import { SwitchRow } from "./SwitchRow";
import { SwitchSection } from "./SwitchSection";
import { TemplateText } from "./TemplateText";
import { textFieldLength } from "./text-length";
import { TemplateTextField, type TemplateTextFieldVariable, type TemplateTextFieldMessages } from "./TemplateTextField";
import { TimeoutDurationRangeFields, type TimeoutDurationRangeValue } from "./TimeoutDurationRangeFields";
import { RoleThreshold } from "./RoleThreshold";
import { RuleNote } from "./RuleNote";

const emptyMessagePreview = (): string => "";

type BivariantSettingsCustomFieldRender<Settings> = {
  bivarianceHack(args: SettingsCustomFieldProps<Settings>): ReactElement | null;
}["bivarianceHack"];

export type SettingsFieldSpec<Settings> =
  | { kind: "number"; key: keyof Settings & string; unit?: string; min: number; max: number; step: number; validate?: (value: number, settings: Settings) => boolean }
  | { kind: "timeoutDurationRange"; key: keyof Settings & string; min: number; max: number; showLabels?: boolean }
  | { kind: "text"; key: keyof Settings & string; prefix?: string; maxLength?: number; lengthUnit?: "utf16" | "codePoints"; optional?: boolean; validate?: (value: string) => boolean }
  | { kind: "template"; key: keyof Settings & string; minRows?: number; optional?: boolean; preview: (template: string, samples: Readonly<Record<string, string>>) => string }
  | { kind: "chatTarget"; key: keyof Settings & string; allowed?: readonly (ChatOutputTarget | "channel_default")[] }
  | { kind: "message"; key: keyof Settings & string; enabledKey: keyof Settings & string; targetKey?: keyof Settings & string; allowedTargets?: readonly (ChatOutputTarget | "channel_default")[]; preview: (template: string, samples: Readonly<Record<string, string>>) => string }
  | { kind: "select"; key: keyof Settings & string; options: readonly { value: string }[] }
  | { kind: "roleThreshold"; key: keyof Settings & string; options: readonly { value: string }[] }
  | { kind: "segment"; key: keyof Settings & string; options: readonly { value: string | number }[] }
  | { kind: "choice"; key: keyof Settings & string; options: readonly { value: string; icon?: IconName }[] }
  | { kind: "custom"; key: keyof Settings & string; render: BivariantSettingsCustomFieldRender<Settings> }
  | { kind: "switchCard"; key: keyof Settings & string; children?: readonly SettingsFieldSpec<Settings>[] };

export interface SettingsCustomFieldProps<Settings> {
  settings: Settings;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled: boolean;
  readOnly: boolean;
  error?: string;
  fieldErrors?: Readonly<Record<string, string>>;
  label: string;
  hint: string;
  context?: unknown;
}

export interface SettingsEditorSpec<Settings> {
  sections: readonly { id: string; icon: IconName; fields: readonly SettingsFieldSpec<Settings>[]; note?: string }[];
}

interface SettingsFieldText {
  label: string;
  hint: string;
  unit?: string;
  zeroValueLabel?: string;
  placeholder?: string;
  invalidError?: string;
  requiredError?: string;
  optionalHint?: string;
  description?: string;
  disabledReason?: string;
  minimumLabel?: string;
  maximumLabel?: string;
  options?: Readonly<Record<string, { label: string; description?: string }>>;
  countLabel?: (count: number, maxLength: number) => string;
  previewLabel?: string;
  previewSpeaker?: string;
  previewEmptyText?: string;
  variables?: readonly TemplateTextFieldVariable[];
}

interface SettingsEditorTexts {
  sections: Readonly<Record<string, string>>;
  fields: Readonly<Record<string, SettingsFieldText>>;
  notes?: Readonly<Record<string, string>>;
}

export interface SettingsEditorCatalog extends SettingsEditorTexts {
  title: string;
  ariaLabel: string;
  readOnlyReason: string;
  urlSaveError?: string;
  saveLabel: string;
  discardLabel: string;
  savedLabel: string;
  pendingLabel: string;
  invalidMessage: string;
  numberMissing: string;
  issueLabels: { error: string; warning: string };
  saveError: string;
  conflictMessage: string;
  reloadLabel: string;
  enabledLabel: string;
  disabledLabel: string;
  messageEnabledLabel?: string;
  templateMessages: TemplateTextFieldMessages;
  warningLabel: (warning: PanelTemplateWarning) => string;
}

export interface SettingsEditorDefinition<Settings> {
  spec: SettingsEditorSpec<Settings>;
  locales: Readonly<Record<"de" | "en", SettingsEditorCatalog>>;
  /** Groups page editor sections into stable dashboard columns. */
  pageColumns?: readonly (readonly string[])[];
  registeredTemplateVariableFields?: readonly (keyof Settings & string)[];
  layout?: "tabs" | "columns";
  savePosition?: "header" | "footer" | "page-header";
  showTitle?: boolean;
  className?: string;
  /** Message paths handled by a custom field rather than the generic spec. */
  messageFields?: readonly MessageEnabledField[];
}

export interface SettingsEditorProps<Settings extends object> {
  spec: SettingsEditorSpec<Settings>;
  sectionId: string;
  settings: Settings;
  onChange: <Key extends keyof Settings>(key: Key, value: Settings[Key]) => void;
  texts: SettingsEditorTexts;
  variables?: Readonly<Partial<Record<keyof Settings & string, readonly TemplateTextFieldVariable[]>>>;
  templateMetadata?: Readonly<Partial<Record<keyof Settings & string, readonly (TemplateTextFieldVariable & { maxLength: number; fallbackWhenAbsent?: number })[]>>>;
  templateMessages: TemplateTextFieldMessages;
  readOnly?: boolean;
  readOnlySectionOnly?: boolean;
  disabled?: boolean;
  clampNumberOnBlur?: boolean;
  enabledLabel?: string;
  disabledLabel?: string;
  messageEnabledLabel?: string;
  fieldErrors?: Readonly<Record<string, string>>;
  onIssuesChange?: (key: keyof Settings & string, issues: { unknown: readonly string[]; worstCaseExceeded: boolean }) => void;
  idPrefix?: string;
  effectiveDefaultChatTarget?: ChatOutputTarget;
  customContext?: unknown;
  afterNote?: ReactNode;
  hiddenNotes?: readonly string[];
}

export function SettingsEditor<Settings extends object>({
  spec,
  sectionId,
  settings,
  onChange,
  texts,
  variables,
  templateMetadata,
  templateMessages,
  readOnly = false,
  readOnlySectionOnly = false,
  disabled = false,
  clampNumberOnBlur = true,
  enabledLabel,
  disabledLabel,
  messageEnabledLabel,
  fieldErrors = {},
  onIssuesChange,
  idPrefix = "settings",
  effectiveDefaultChatTarget,
  customContext,
  afterNote,
  hiddenNotes = [],
}: SettingsEditorProps<Settings>): ReactElement {
  const section = spec.sections.find((candidate) => candidate.id === sectionId);
  const renderNote = (note: string | undefined): ReactElement | null => {
    if (note === undefined || texts.notes?.[note] === undefined) return null;
    const hidden = hiddenNotes.includes(note);
    if (hidden) return <div className="ui-settings-editor__hidden-note" aria-hidden="true">
      <RuleNote>{texts.notes[note]}</RuleNote>
    </div>;
    return <RuleNote>{texts.notes[note]}</RuleNote>;
  };

  const renderField = (field: SettingsFieldSpec<Settings>): ReactElement | null => {
    const copy = texts.fields[field.key];
    if (copy === undefined) return null;
    const id = `${idPrefix}-${field.key}`;
    const fieldValue = settings[field.key];
    const error = fieldErrors[field.key];
    if (field.kind === "custom") return <Fragment key={field.key}>{field.render({
      settings,
      value: fieldValue,
      onChange: (next) => { onChange(field.key, next as Settings[typeof field.key]); },
      disabled,
      readOnly,
      ...(error === undefined ? {} : { error }),
      fieldErrors,
      label: copy.label,
      hint: copy.hint,
      ...(customContext === undefined ? {} : { context: customContext }),
    })}</Fragment>;
    if (readOnly) return renderReadOnlyField(field, copy, fieldValue, enabledLabel, disabledLabel);
    if (field.kind === "chatTarget") {
      return (
        <ChatTargetField
          key={field.key}
          label={copy.label}
          value={fieldValue === null || fieldValue === undefined || fieldValue === "channel_default" ? null : String(fieldValue) as ChatOutputTarget}
          onChange={(next) => { onChange(field.key, (next ?? (field.allowed?.includes("channel_default") ? "channel_default" : null)) as Settings[typeof field.key]); }}
          allowed={field.allowed ?? ["all_chats", "source_only"]}
          {...(effectiveDefaultChatTarget === undefined ? {} : { effectiveDefault: effectiveDefaultChatTarget })}
          disabled={disabled}
          reserveError
          {...(error === undefined ? {} : { error })}
        />
      );
    }
    if (field.kind === "select" || field.kind === "roleThreshold") {
      if (field.kind === "roleThreshold") {
        return <InspectorFieldRow key={field.key} label={copy.label} help={copy.hint}>
          <RoleThreshold
            id={id}
            label={copy.label}
            value={typeof fieldValue === "string" ? fieldValue : ""}
            onChange={(next) => { onChange(field.key, next as Settings[typeof field.key]); }}
            options={field.options.map(({ value }) => ({
              value,
              label: copy.options?.[value]?.label ?? "",
              inclusion: copy.options?.[value]?.description ?? "",
            }))}
            disabled={disabled}
            {...(error === undefined ? {} : { error })}
          />
        </InspectorFieldRow>;
      }
      const options = field.options.map(({ value }) => ({ value, label: copy.options?.[value]?.label ?? "" }));
      return (
        <InspectorFieldRow key={field.key} label={copy.label} help={copy.hint}>
          <Select
            id={id}
            label={copy.label}
            hint={copy.hint}
            {...(error === undefined ? {} : { error })}
            value={typeof fieldValue === "string" ? fieldValue : null}
            onChange={(next) => { if (next !== null) onChange(field.key, next as Settings[typeof field.key]); }}
            options={options}
            required
            disabled={disabled}
          />
        </InspectorFieldRow>
      );
    }
    if (field.kind === "number") {
      const unit = typeof fieldValue === "number" && fieldValue === 0
        ? copy.zeroValueLabel ?? copy.unit ?? field.unit
        : copy.unit ?? field.unit;
      return <UnitInput
        key={field.key}
        id={id}
        label={copy.label}
        {...(unit === undefined ? {} : { unit })}
        hint={copy.hint}
        {...(error === undefined ? {} : { error })}
        min={field.min}
        max={field.max}
        step={field.step}
        value={typeof fieldValue === "number" ? fieldValue : ""}
        disabled={disabled}
        clampOnBlur={clampNumberOnBlur}
        onChange={(next) => { onChange(field.key, next as Settings[typeof field.key]); }}
      />;
    }
    if (field.kind === "timeoutDurationRange") {
      const range = fieldValue !== null && typeof fieldValue === "object"
        ? fieldValue as unknown as TimeoutDurationRangeValue
        : { minSeconds: "" as const, maxSeconds: "" as const };
      return (
        <InspectorFieldRow key={field.key} label={copy.label} help={copy.hint}>
          <TimeoutDurationRangeFields
            idPrefix={id}
            value={range}
            onChange={(next) => { onChange(field.key, next as Settings[typeof field.key]); }}
            min={field.min}
            max={field.max}
            minimumLabel={copy.minimumLabel ?? copy.label}
            maximumLabel={copy.maximumLabel ?? copy.label}
            hint={copy.hint}
            {...(field.showLabels === undefined ? {} : { showLabels: field.showLabels })}
            disabled={disabled}
            {...(copy.unit === undefined ? {} : { unit: copy.unit })}
            {...(error === undefined ? {} : { error })}
          />
        </InspectorFieldRow>
      );
    }
    if (field.kind === "text") {
      const maxLength = field.maxLength;
      return (
        <InspectorFieldRow key={field.key} label={copy.label} help={copy.hint}>
          <Field
            id={id}
            label={copy.label}
            {...(error === undefined ? {} : { error })}
            // The hint lives in the row's info popover, so a counted field shares one 18px line between counter and error.
            errorInDescription={maxLength !== undefined}
            value={typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(next) => { onChange(field.key, next as Settings[typeof field.key]); }}
            required={!field.optional}
            {...(copy.placeholder === undefined ? {} : { placeholder: copy.placeholder })}
            {...(field.prefix === undefined ? {} : { prefix: field.prefix })}
            {...(maxLength === undefined ? {} : { countLength: (text: string) => textFieldLength(field, text), maxLength, countLabel: copy.countLabel ?? ((count, maximum) => `${String(count)} / ${String(maximum)}`) })}
          />
        </InspectorFieldRow>
      );
    }
    if (field.kind === "template") {
      const metadata = templateMetadata?.[field.key];
      const fieldVariables = variables?.[field.key] ?? metadata?.map(({ name, description, sample }) => ({ name, description, sample }));
      return (
        <InspectorFieldRow key={field.key} label={copy.label} help={copy.hint} className="inspector-field-row--stacked inspector-field-row--template">
          <TemplateTextField
            id={id}
            name={field.key}
            label={copy.label}
            hint={copy.hint}
            {...(error === undefined ? {} : { error })}
            value={typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(next) => { onChange(field.key, next as Settings[typeof field.key]); }}
            maxLength={500}
            {...(metadata === undefined ? {} : { worstCaseLength: worstCaseTemplateLength(String(fieldValue ?? ""), metadata) })}
            {...(fieldVariables === undefined ? {} : { variables: fieldVariables })}
            preview={field.preview}
            required={!field.optional}
            onIssuesChange={(issues) => { onIssuesChange?.(field.key, issues); }}
            {...(copy.previewLabel === undefined ? {} : { previewLabel: copy.previewLabel })}
            {...(copy.previewSpeaker === undefined ? {} : { previewSpeaker: copy.previewSpeaker })}
            {...(copy.previewEmptyText === undefined ? {} : { previewEmptyText: copy.previewEmptyText })}
            {...(field.minRows === undefined ? {} : { minRows: field.minRows })}
            messages={templateMessages}
          />
        </InspectorFieldRow>
      );
    }
    if (field.kind === "message") {
      const targetKey = field.targetKey;
      const targetCopy = targetKey === undefined ? undefined : texts.fields[targetKey];
      const template = typeof fieldValue === "string" ? fieldValue : "";
      const messageEnabled = settings[field.enabledKey] === true;
      const targetValue = targetKey === undefined ? undefined : settings[targetKey];
      const targetError = targetKey === undefined ? undefined : fieldErrors[targetKey];
      const fieldVariables = variables?.[field.key] ?? templateMetadata?.[field.key]?.map(({ name, description, sample }) => ({ name, description, sample }));
      const metadata = templateMetadata?.[field.key];
      return <div key={field.key} className="ui-settings-editor__message">
        <div className="ui-settings-editor__message-heading">
          <strong>{copy.label}</strong>
          <div className="ui-settings-editor__message-toggle">
            <SwitchRow
              label={messageEnabledLabel ?? copy.label}
              ariaLabel={`${copy.label}: ${messageEnabledLabel ?? copy.label}`}
              checked={messageEnabled}
              onChange={(next) => { onChange(field.enabledKey, next as Settings[typeof field.enabledKey]); }}
              disabled={disabled}
            />
          </div>
        </div>
        <TemplateTextField
          id={id}
          name={field.key}
          label={copy.label}
          hint={copy.hint}
          {...(error === undefined ? {} : { error })}
          value={template}
          disabled={disabled}
          onChange={(next) => { onChange(field.key, next as Settings[typeof field.key]); }}
          maxLength={500}
          {...(metadata === undefined ? {} : { worstCaseLength: worstCaseTemplateLength(template, metadata) })}
          {...(fieldVariables === undefined ? {} : { variables: fieldVariables })}
          preview={messageEnabled ? field.preview : emptyMessagePreview}
          required={messageEnabled}
          onIssuesChange={(issues) => { onIssuesChange?.(field.key, issues); }}
          {...(copy.previewLabel === undefined ? {} : { previewLabel: copy.previewLabel })}
          {...(copy.previewSpeaker === undefined ? {} : { previewSpeaker: copy.previewSpeaker })}
          {...(copy.previewEmptyText === undefined ? {} : { previewEmptyText: copy.previewEmptyText })}
          minRows={2}
          messages={templateMessages}
        />
        {targetCopy === undefined || targetKey === undefined ? null : <ChatTargetField
          label={targetCopy.label}
          value={targetValue === null || targetValue === undefined || targetValue === "channel_default" ? null : String(targetValue) as ChatOutputTarget}
          onChange={(next) => { onChange(targetKey, (next ?? (field.allowedTargets?.includes("channel_default") ? "channel_default" : null)) as Settings[typeof targetKey]); }}
          allowed={field.allowedTargets ?? ["all_chats", "source_only"]}
          {...(effectiveDefaultChatTarget === undefined ? {} : { effectiveDefault: effectiveDefaultChatTarget })}
          disabled={disabled}
          reserveError
          {...(targetError === undefined ? {} : { error: targetError })}
        />}
      </div>;
    }
    if (field.kind === "segment" || field.kind === "choice") {
      if (field.kind === "segment") {
        const options = field.options.map((option) => ({ value: String(option.value), label: copy.options?.[String(option.value)]?.label ?? "" }));
        return (
          <InspectorFieldRow key={field.key} label={copy.label} help={copy.options?.[String(fieldValue)]?.description ?? copy.hint}>
            <SegmentChoice
              label={copy.label}
              value={String(fieldValue ?? "")}
              onChange={(next) => {
                const selected = field.options.find((option) => String(option.value) === next)?.value ?? next;
                onChange(field.key, selected as Settings[typeof field.key]);
              }}
              options={options}
              disabled={disabled}
              reserveError
              {...(error === undefined ? {} : { error })}
            />
          </InspectorFieldRow>
        );
      }
      const options = field.options.map((option) => ({
        value: option.value,
        label: copy.options?.[option.value]?.label ?? "",
        description: copy.options?.[option.value]?.description ?? "",
        ...(option.icon === undefined ? {} : { icon: option.icon }),
      }));
      return (
        <InspectorFieldRow key={field.key} label={copy.label} help={copy.hint}>
          <ChoiceCards
            label={copy.label}
            value={String(fieldValue ?? "")}
            onChange={(next) => { onChange(field.key, next as Settings[typeof field.key]); }}
            options={options}
            disabled={disabled}
            reserveError
            {...(error === undefined ? {} : { error })}
          />
        </InspectorFieldRow>
      );
    }
    const children = field.children?.map(renderField) ?? [];
    const lockedReason = field.children === undefined ? copy.disabledReason : undefined;
    const checked = fieldValue === true;
    const onToggle = (next: boolean): void => { onChange(field.key, next as Settings[typeof field.key]); };
    const fieldError = <FieldError className="ui-field__error" message={error} />;
    if (field.children === undefined) return <div key={field.key}>
      <SwitchRow
        label={copy.label}
        description={copy.description ?? copy.hint}
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        {...(lockedReason === undefined ? {} : { lockedReason })}
      />
      {fieldError}
    </div>;
    return <div key={field.key}>
      <SwitchSection
        id={id}
        title={copy.label}
        hint={copy.description ?? copy.hint}
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        {...(lockedReason === undefined ? {} : { lockedReason })}
      >{children as ReactElement[]}</SwitchSection>
      {fieldError}
    </div>;
  };

  const renderReadOnlyProperty = (field: SettingsFieldSpec<Settings>): ReactElement | null => {
    const copy = texts.fields[field.key];
    if (copy === undefined) return null;
    const value = settings[field.key];
    if (field.kind === "custom") return <Fragment key={field.key}>{field.render({
      settings,
      value,
      onChange: () => undefined,
      disabled: true,
      readOnly: true,
      label: copy.label,
      hint: copy.hint,
      ...(customContext === undefined ? {} : { context: customContext }),
    })}</Fragment>;
    if (field.kind === "switchCard" && field.children !== undefined) {
      const switchEnabled = value === true;
      return <div key={field.key}>
        <dt>{copy.label}</dt>
        <dd>
          <span className="ui-settings-editor__readonly-switch">{switchEnabled ? enabledLabel : disabledLabel}</span>
          <dl className="ui-settings-editor__readonly-children">
            {field.children.map(renderReadOnlyProperty)}
          </dl>
        </dd>
      </div>;
    }
    if (field.kind === "message") {
      const targetCopy = field.targetKey === undefined ? undefined : texts.fields[field.targetKey];
      const template = typeof value === "string" ? value : "";
      const messageEnabled = settings[field.enabledKey] === true;
      const target = field.targetKey === undefined ? undefined : settings[field.targetKey];
      const targetLabel = targetCopy?.options?.[String(target)]?.label ?? String(target ?? "");
      return <div className="ui-settings-editor__readonly-message" key={field.key}>
        <dt>{copy.label}</dt>
        <dd>
          <strong>{messageEnabled ? enabledLabel : disabledLabel}</strong>
          <TemplateText value={template} variables={copy.variables ?? []} />
          {targetCopy === undefined || field.targetKey === undefined ? null : <span>{targetCopy.label}: {targetLabel}</span>}
        </dd>
      </div>;
    }
    return <div key={field.key}><dt>{copy.label}</dt><dd>{renderReadOnlyField(field, copy, value, enabledLabel, disabledLabel)}</dd></div>;
  };

  if (readOnly) {
    return readOnlySectionOnly
      ? <InspectorSection title={section === undefined ? "" : texts.sections[section.id]}>
        {renderNote(section?.note)}
        {afterNote}
        <dl className="properties ui-settings-editor__properties">
          {section?.fields.map(renderReadOnlyProperty)}
        </dl>
      </InspectorSection>
      : <div className="ui-settings-editor__readonly-sections">
        {spec.sections.map((candidate) => <InspectorSection title={texts.sections[candidate.id]} key={candidate.id}>
          {renderNote(candidate.note)}
          {candidate.id === sectionId ? afterNote : null}
          <dl className="properties ui-settings-editor__properties">
            {candidate.fields.map(renderReadOnlyProperty)}
          </dl>
        </InspectorSection>)}
      </div>;
  }

  return (
    <div data-ui="" className="ui-settings-editor" data-section={sectionId} aria-label={section === undefined ? undefined : texts.sections[section.id]}>
      {section === undefined ? null : <InspectorSection title={texts.sections[section.id]}>
        {renderNote(section.note)}
        {afterNote}
        {section.fields.map(renderField)}
      </InspectorSection>}
    </div>
  );
}

const renderReadOnlyField = <Settings extends object>(
  field: SettingsFieldSpec<Settings>,
  copy: SettingsFieldText,
  value: Settings[keyof Settings],
  enabledLabel: string | undefined,
  disabledLabel: string | undefined,
): ReactElement => {
  if (field.kind === "template") {
    const vars = copy.variables ?? [];
    return <TemplateText value={typeof value === "string" ? value : ""} variables={vars} />;
  }
  if (field.kind === "message") return <>{String(value)}</>;
  if (field.kind === "switchCard") return <>{value === true ? enabledLabel : disabledLabel}</>;
  if (field.kind === "number") {
    if (typeof value === "number" && value === 0 && copy.zeroValueLabel !== undefined) return <>{copy.zeroValueLabel}</>;
    return <>{String(value)}{copy.unit === undefined ? "" : ` ${copy.unit}`}</>;
  }
  if (field.kind === "timeoutDurationRange") {
    const range = value !== null && typeof value === "object" ? value as unknown as TimeoutDurationRangeValue : null;
    if (range === null) return <></>;
    return range.minSeconds === range.maxSeconds
      ? <>{String(range.minSeconds)} {copy.unit ?? "s"}</>
      : <>{String(range.minSeconds)}–{String(range.maxSeconds)} {copy.unit ?? "s"}</>;
  }
  if (field.kind === "text") return <>{field.prefix ?? ""}{String(value)}</>;
  if (field.kind === "chatTarget") return <>{copy.options?.[String(value)]?.label ?? String(value)}</>;
  return <>{copy.options?.[String(value)]?.label ?? String(value)}</>;
};
